// Operator › Templates › a version (`/operator/templates/$templateId/versions/$versionId`; DC-03,
// DC-02 validation): the version's facts, standing orders, role-to-model map, evals with their
// last result, the rule → enforcer table, the reasons its last validation stored
// (`validation_report.reasons`) and the edit proposals that touch it. Three calls on load: the
// version (`GET /v1/operator/template-versions/{id}`), its template (name, sibling versions) and
// the template's proposals.
//
// Template authors move it through its lifecycle: a draft is edited (PATCH), validated (a failed
// validation is a 200 that leaves it a draft with reasons), or validated and published in one
// go; a validated version is published (422 `publish_refused` lists why not); a published one is
// deprecated, optionally naming another published version as its replacement.
import { newIdempotencyKey } from "@companyos/sdk";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Field,
  FieldDescription,
  FieldLabel,
  LocationHeader,
  NativeSelect,
  NativeSelectOption,
  Notice,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { type ReactNode, useId, useState } from "react";
import type { Api } from "../../../api/client";
import type { AgentTemplateVersion, MeResponse, TemplateVersionDetail } from "../../../api/types";
import { formatDateTime } from "../../format";
import { OpsDoc, OpsFrame, operatorCrumb } from "../../ops-ux";
import { Facts, LoadError, Loading, Nothing, Section } from "../../screen-parts";
import { ProposalsTable } from "./template-detail";
import {
  AuthorOnlyNote,
  isTemplateAuthor,
  leadModel,
  mistakeText,
  problemLines,
  reportReasons,
  SOURCE_LABEL,
  VersionStateBadge,
  versionLabel,
} from "./template-parts";
import {
  agentTemplateQuery,
  templateKeys,
  templateProposalsQuery,
  templateVersionQuery,
} from "./template-queries";
import { VersionFormDialog } from "./version-form";

/** What the last action said, shown above the facts until the next one. */
type Outcome = { tone: "success" | "danger"; title: string; lines: string[] };

export function VersionDetail({
  api,
  me,
  templateId,
  versionId,
}: {
  api: Api;
  me: MeResponse;
  templateId: string;
  versionId: string;
}) {
  const detail = useQuery(templateVersionQuery(api, versionId));
  const template = useQuery(agentTemplateQuery(api, templateId));
  const proposals = useQuery(templateProposalsQuery(api, templateId));
  const author = isTemplateAuthor(me);
  const name = template.data?.template.name ?? "Template";
  const crumbs = [
    operatorCrumb,
    { label: "Templates", render: <Link to="/operator/templates" /> },
    {
      label: name,
      render: <Link to="/operator/templates/$templateId" params={{ templateId }} />,
    },
  ];

  if (detail.isPending)
    return (
      <OpsFrame>
        <LocationHeader crumbs={crumbs} title="Version" />
        <OpsDoc>
          <Loading label="Loading the version" />
        </OpsDoc>
      </OpsFrame>
    );
  if (detail.isError)
    return (
      <OpsFrame>
        <LocationHeader crumbs={crumbs} title="Version" />
        <OpsDoc>
          <LoadError what="The version" error={detail.error} />
        </OpsDoc>
      </OpsFrame>
    );

  const { version: v, evals, rules } = detail.data;
  const siblings = template.data?.versions ?? [];
  const current = template.data?.template.current_version_id === v.id;
  const reasons = reportReasons(v);
  const mine = (proposals.data?.data ?? []).filter(
    (p) => p.based_on_version_id === v.id || p.accepted_version_id === v.id,
  );
  const map = Object.entries(v.role_model_map);

  return (
    <VersionActions api={api} detail={detail.data} siblings={siblings} author={author}>
      {(actions, outcome) => (
        <OpsFrame>
          <LocationHeader
            crumbs={crumbs}
            title={`Version ${v.version}`}
            badge={
              <span className="flex items-center gap-1.5">
                <VersionStateBadge state={v.state} />
                {current ? <Badge variant="accent">Current</Badge> : null}
              </span>
            }
            actions={actions}
          />
          <OpsDoc>
            <p className="mb-4 text-xs text-muted-foreground" data-slot="version-facts">
              {v.changelog ?? "No changelog."}
            </p>
            {author ? null : <AuthorOnlyNote />}
            {outcome ? (
              <Notice
                tone={outcome.tone}
                title={outcome.title}
                role={outcome.tone === "danger" ? "alert" : "status"}
                data-slot="version-outcome"
                className="mb-4"
              >
                {outcome.lines.length ? (
                  <ul className="list-disc pl-5">
                    {outcome.lines.map((l) => (
                      <li key={l}>{l}</li>
                    ))}
                  </ul>
                ) : null}
              </Notice>
            ) : null}
            {v.state === "draft" && reasons.length > 0 ? (
              <Section
                id="version-validation"
                title="Last validation"
                description="Why the draft did not validate. Fix these, then validate again."
              >
                <Notice tone="warning" data-slot="validation-reasons">
                  <ul className="list-disc pl-5">
                    {reasons.map((r) => (
                      <li key={r}>{r}</li>
                    ))}
                  </ul>
                </Notice>
              </Section>
            ) : null}
            <Section id="version-facts" title="Version">
              <Facts
                items={[
                  { label: "State", value: <VersionStateBadge state={v.state} /> },
                  { label: "Version", value: `v${v.version}` },
                  { label: "Source", value: SOURCE_LABEL[v.source] },
                  {
                    label: "Supersedes",
                    value: v.supersedes_version_id ? (
                      <Link
                        to="/operator/templates/$templateId/versions/$versionId"
                        params={{ templateId, versionId: v.supersedes_version_id }}
                        className="underline-offset-4 hover:underline"
                      >
                        {versionLabel(siblings, v.supersedes_version_id)}
                      </Link>
                    ) : (
                      "—"
                    ),
                  },
                  {
                    label: "Skill index",
                    value: v.skill_index_ref ? (
                      <code className="text-xs break-all">{v.skill_index_ref}</code>
                    ) : (
                      "None"
                    ),
                  },
                  {
                    label: "Skill index tokens",
                    value: `${v.skill_index_tokens ?? "not measured"} / cap ${v.skill_index_token_cap}`,
                  },
                  {
                    label: "Verification skill",
                    value: v.verification_skill_pack_id ? (
                      <code className="text-xs">{v.verification_skill_pack_id}</code>
                    ) : (
                      "None (required to publish)"
                    ),
                  },
                  { label: "Lead model", value: leadModel(v.role_model_map) ?? "None" },
                  {
                    label: "Content hash",
                    value: v.content_hash ? (
                      <code className="text-xs" title={v.content_hash}>
                        {v.content_hash.slice(0, 16)}…
                      </code>
                    ) : (
                      "Set on validation"
                    ),
                  },
                  {
                    label: "Validated",
                    value: v.validated_at ? formatDateTime(v.validated_at) : "—",
                  },
                  {
                    label: "Published",
                    value: v.published_at ? formatDateTime(v.published_at) : "—",
                  },
                  {
                    label: "Deprecated",
                    value: v.deprecated_at ? formatDateTime(v.deprecated_at) : "—",
                  },
                ]}
              />
            </Section>
            <Section id="version-orders" title="Standing orders">
              <pre
                data-slot="standing-orders"
                className="rounded-md border border-border bg-sunken p-3 text-sm whitespace-pre-wrap break-words"
              >
                {v.standing_orders_text || "None."}
              </pre>
            </Section>
            <Section id="version-roles" title="Role-to-model map">
              {map.length === 0 ? (
                <Nothing title="No roles" />
              ) : (
                <Table data-slot="version-roles">
                  <TableHeader>
                    <TableRow>
                      <TableHead scope="col">Role</TableHead>
                      <TableHead scope="col">Model</TableHead>
                      <TableHead scope="col">Paths</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {map.map(([role, e]) => (
                      <TableRow key={role}>
                        <TableHead scope="row" className="font-normal">
                          <code className="text-xs">{role}</code>
                        </TableHead>
                        <TableCell>{e.model}</TableCell>
                        <TableCell>{e.paths.join(" → ")}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Section>
            <Section
              id="version-evals"
              title="Evals"
              description="Every eval must pass for the version to validate and publish."
            >
              {evals.length === 0 ? (
                <Nothing title="No evals">A version needs at least one passing eval.</Nothing>
              ) : (
                <Table data-slot="version-evals">
                  <TableHeader>
                    <TableRow>
                      <TableHead scope="col">Key</TableHead>
                      <TableHead scope="col">Kind</TableHead>
                      <TableHead scope="col">Spec</TableHead>
                      <TableHead scope="col">Proves</TableHead>
                      <TableHead scope="col">Last result</TableHead>
                      <TableHead scope="col">Last run</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {evals.map((e) => (
                      <TableRow key={e.id} data-eval={e.key}>
                        <TableHead scope="row" className="font-normal">
                          <code className="text-xs">{e.key}</code>
                        </TableHead>
                        <TableCell>{e.kind}</TableCell>
                        <TableCell className="max-w-xs break-all">
                          <code className="text-xs">{e.spec_ref}</code>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {e.proves_mistake_ref ? mistakeText(e.proves_mistake_ref) : "—"}
                        </TableCell>
                        <TableCell>
                          {e.last_result ? (
                            <Badge
                              variant={
                                e.last_result === "pass"
                                  ? "success"
                                  : e.last_result === "fail"
                                    ? "danger"
                                    : "warning"
                              }
                            >
                              {e.last_result}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">Not run</span>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {e.last_run_at ? formatDateTime(e.last_run_at) : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Section>
            <Section
              id="version-rules"
              title="Rules and enforcers"
              description="Each rule names what enforces it and the past mistake it was proven against."
            >
              {rules.length === 0 ? (
                <Nothing title="No rules" />
              ) : (
                <Table data-slot="version-rules">
                  <TableHeader>
                    <TableRow>
                      <TableHead scope="col">Key</TableHead>
                      <TableHead scope="col">Rule</TableHead>
                      <TableHead scope="col">Enforcer</TableHead>
                      <TableHead scope="col">Proven against</TableHead>
                      <TableHead scope="col">Why only written</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rules.map((r) => (
                      <TableRow key={r.id} data-rule={r.key}>
                        <TableHead scope="row" className="font-normal">
                          <code className="text-xs">{r.key}</code>
                        </TableHead>
                        <TableCell className="max-w-sm">{r.rule_text}</TableCell>
                        <TableCell>
                          {r.enforcer_kind.replace("_", " ")} ·{" "}
                          <code className="text-xs break-all">{r.enforcer_ref}</code>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {mistakeText(r.proven_against_ref)}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {r.reason_if_written ?? "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Section>
            <Section id="version-proposals" title="Edit proposals for this version">
              {proposals.isPending ? (
                <Loading label="Loading edit proposals" rows={2} />
              ) : proposals.isError ? (
                <LoadError what="Edit proposals" error={proposals.error} />
              ) : (
                <ProposalsTable proposals={mine} versions={siblings} />
              )}
            </Section>
          </OpsDoc>
        </OpsFrame>
      )}
    </VersionActions>
  );
}

/**
 * The lifecycle buttons for the version's state (authors only) and the outcome of the last one;
 * renders its children with both so the page header carries the buttons.
 */
function VersionActions({
  api,
  detail,
  siblings,
  author,
  children,
}: {
  api: Api;
  detail: TemplateVersionDetail;
  siblings: AgentTemplateVersion[];
  author: boolean;
  children: (actions: ReactNode, outcome: Outcome | null) => ReactNode;
}) {
  const qc = useQueryClient();
  const v = detail.version;
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [editing, setEditing] = useState(false);
  const versionKey = templateKeys.version(v.id);

  const refresh = () => qc.invalidateQueries({ queryKey: templateKeys.all() });
  const storeVersion = (next: AgentTemplateVersion) =>
    qc.setQueryData<TemplateVersionDetail>(versionKey, (old) =>
      old ? { ...old, version: next } : old,
    );
  const validated = (d: TemplateVersionDetail): Outcome =>
    d.version.state === "validated"
      ? {
          tone: "success",
          title: `v${d.version.version} validated`,
          lines: ["The version is frozen. Publish it to make it the template's current version."],
        }
      : {
          tone: "danger",
          title: "Validation failed: the version stays a draft",
          lines: reportReasons(d.version),
        };
  const published = (p: AgentTemplateVersion): Outcome => ({
    tone: "success",
    title: `v${p.version} published`,
    lines: ["It is now the template's current version; new deploys use it."],
  });
  const refused = (error: unknown, what: string): Outcome => ({
    tone: "danger",
    title: what,
    lines: problemLines(error, "The request failed."),
  });

  const validate = useMutation({
    mutationFn: () =>
      api.client.operator.templateVersions.validate({
        params: { version_id: v.id },
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: async (d) => {
      qc.setQueryData(versionKey, d);
      setOutcome(validated(d));
      await refresh();
    },
    onError: (e) => setOutcome(refused(e, "Validation could not run")),
  });
  const publish = useMutation({
    mutationFn: () =>
      api.client.operator.templateVersions.publish({
        params: { version_id: v.id },
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: async (p) => {
      storeVersion(p);
      setOutcome(published(p));
      await refresh();
    },
    onError: (e) => setOutcome(refused(e, "Publish refused")),
  });
  /** Validate, and publish only when the result is `validated`; stop with the reasons otherwise. */
  const both = useMutation({
    mutationFn: async (): Promise<Outcome> => {
      const d = await api.client.operator.templateVersions.validate({
        params: { version_id: v.id },
        idempotencyKey: newIdempotencyKey(),
      });
      qc.setQueryData(versionKey, d);
      if (d.version.state !== "validated") return validated(d);
      try {
        const p = await api.client.operator.templateVersions.publish({
          params: { version_id: v.id },
          idempotencyKey: newIdempotencyKey(),
        });
        storeVersion(p);
        return published(p);
      } catch (e) {
        return refused(e, `v${d.version.version} validated, but publish was refused`);
      }
    },
    onSuccess: async (o) => {
      setOutcome(o);
      await refresh();
    },
    onError: (e) => setOutcome(refused(e, "Validation could not run")),
  });
  const busy = validate.isPending || publish.isPending || both.isPending;

  let actions: ReactNode = null;
  if (author && v.state === "draft")
    actions = (
      <>
        <Button size="lg" disabled={busy} onClick={() => both.mutate()}>
          {both.isPending ? "Validating…" : "Validate and publish"}
        </Button>
        <Button variant="secondary" disabled={busy} onClick={() => validate.mutate()}>
          {validate.isPending ? "Validating…" : "Validate"}
        </Button>
        <Button variant="ghost" disabled={busy} onClick={() => setEditing(true)}>
          Edit draft
        </Button>
      </>
    );
  else if (author && v.state === "validated")
    actions = (
      <Button size="lg" disabled={busy} onClick={() => publish.mutate()}>
        {publish.isPending ? "Publishing…" : "Publish"}
      </Button>
    );
  else if (author && v.state === "published")
    actions = (
      <DeprecateDialog
        api={api}
        version={v}
        siblings={siblings}
        onDone={async (d) => {
          storeVersion(d);
          setOutcome({
            tone: "success",
            title: `v${d.version} deprecated`,
            lines: [
              "New deploys no longer use it; supervisors of seats on it get a re-deploy task.",
            ],
          });
          await refresh();
        }}
      />
    );

  return (
    <>
      {children(actions, outcome)}
      {author && v.state === "draft" ? (
        <VersionFormDialog
          mode="edit"
          api={api}
          open={editing}
          onOpenChange={setEditing}
          detail={detail}
          onSaved={async (d) => {
            qc.setQueryData(versionKey, d);
            setOutcome({ tone: "success", title: "Draft saved", lines: [] });
            setEditing(false);
            await refresh();
          }}
        />
      ) : null}
    </>
  );
}

function DeprecateDialog({
  api,
  version,
  siblings,
  onDone,
}: {
  api: Api;
  version: AgentTemplateVersion;
  siblings: AgentTemplateVersion[];
  onDone: (v: AgentTemplateVersion) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [replacement, setReplacement] = useState("");
  const [reason, setReason] = useState("");
  const ids = { replacement: useId(), reason: useId() };
  const candidates = siblings.filter((s) => s.state === "published" && s.id !== version.id);
  const deprecate = useMutation({
    mutationFn: () =>
      api.client.operator.templateVersions.deprecate({
        params: { version_id: version.id },
        idempotencyKey: newIdempotencyKey(),
        body: {
          ...(replacement ? { replaced_by_version_id: replacement } : {}),
          ...(reason.trim() ? { reason: reason.trim() } : {}),
        },
      }),
    onSuccess: async (d) => {
      setOpen(false);
      await onDone(d);
    },
  });
  return (
    <AlertDialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) deprecate.reset();
      }}
    >
      <Button variant="destructive" size="lg" onClick={() => setOpen(true)}>
        Deprecate
      </Button>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Deprecate v{version.version}?</AlertDialogTitle>
          <AlertDialogDescription>
            New deploys stop using it, and supervisors of seats running it get a task to re-deploy.
            A deprecated version cannot be published again.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Field>
          <FieldLabel htmlFor={ids.replacement}>Replaced by</FieldLabel>
          <NativeSelect
            id={ids.replacement}
            value={replacement}
            onChange={(e) => setReplacement(e.target.value)}
          >
            <NativeSelectOption value="">No replacement named</NativeSelectOption>
            {candidates.map((c) => (
              <NativeSelectOption key={c.id} value={c.id}>
                v{c.version}
              </NativeSelectOption>
            ))}
          </NativeSelect>
          <FieldDescription>
            {candidates.length
              ? "Another published version of this template."
              : "No other published version to name."}
          </FieldDescription>
        </Field>
        <Field>
          <FieldLabel htmlFor={ids.reason}>Reason</FieldLabel>
          <Textarea
            id={ids.reason}
            rows={2}
            maxLength={2000}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
        {deprecate.isError ? (
          <Notice tone="danger" role="alert">
            {problemLines(deprecate.error, "The version could not be deprecated.").join(" · ")}
          </Notice>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button
            variant="destructive"
            disabled={deprecate.isPending}
            onClick={() => deprecate.mutate()}
          >
            {deprecate.isPending ? "Deprecating…" : "Deprecate version"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
